import { stAnalytics } from "@storytell/analytics";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { GlobalEventData } from "@storytell/types";
import { createSignal, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

type ConnectivityState = "online" | "offline" | "reconnecting" | "restored";

export type ConnectivityService = ReturnType<typeof createConnectivityService>;

interface ConnectivityServiceOptions {
  heartbeatIntervalMs?: number;
  heartbeatTimeoutMs?: number;
  maxConsecutiveFailures?: number;
  performHeartbeat: () => Promise<boolean>;
}

const DEFAULT_HEARTBEAT_INTERVAL_MS = 30000;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 5000;
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 3;
const RESTORED_DISPLAY_DURATION_MS = 2000;

const dispatchConnectivityChanged = (isOnline: boolean, isReconnecting: boolean) => {
  dispatchGlobalEvent("connectivity-changed", { isOnline, isReconnecting });
};

export const createConnectivityService = (options: ConnectivityServiceOptions) => {
  const heartbeatIntervalMs = options.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS;
  const heartbeatTimeoutMs = options.heartbeatTimeoutMs ?? DEFAULT_HEARTBEAT_TIMEOUT_MS;
  const maxConsecutiveFailures = options.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;

  const [state, setState] = createSignal<ConnectivityState>("online");
  const [consecutiveFailures, setConsecutiveFailures] = createSignal(0);

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let restoredTimeout: ReturnType<typeof setTimeout> | null = null;
  let isMounted = false;
  let forceCheckPending = false;

  const clearHeartbeatInterval = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const clearRestoredTimeout = () => {
    if (restoredTimeout) {
      clearTimeout(restoredTimeout);
      restoredTimeout = null;
    }
  };

  const transitionToOnline = () => {
    clearRestoredTimeout();
    setState("online");
    setConsecutiveFailures(0);
    dispatchConnectivityChanged(true, false);
  };

  const transitionToOffline = () => {
    clearRestoredTimeout();
    setState("offline");
    dispatchConnectivityChanged(false, false);
    // Keep heartbeat running to detect when API comes back
    startHeartbeat();
  };

  const transitionToReconnecting = () => {
    clearRestoredTimeout();
    setState("reconnecting");
    dispatchConnectivityChanged(false, true);
    startHeartbeat();
  };

  const transitionToRestored = () => {
    clearHeartbeatInterval();
    clearRestoredTimeout();
    setState("restored");
    setConsecutiveFailures(0);
    dispatchGlobalEvent("connectivity-restored", undefined);
    dispatchConnectivityChanged(true, false);

    restoredTimeout = setTimeout(() => {
      transitionToOnline();
    }, RESTORED_DISPLAY_DURATION_MS);
  };

  const performHeartbeatCheck = async (): Promise<boolean> => {
    try {
      let timeoutId: ReturnType<typeof setTimeout>;
      const timeoutPromise = new Promise<boolean>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Heartbeat timeout")), heartbeatTimeoutMs);
      });

      const result = await Promise.race([
        options.performHeartbeat().finally(() => clearTimeout(timeoutId)),
        timeoutPromise,
      ]);
      return result;
    } catch {
      return false;
    }
  };

  const handleHeartbeatResult = (success: boolean, isInitialCheck = false) => {
    if (!isMounted) return;

    if (success) {
      const currentState = state();
      if (currentState === "reconnecting" || currentState === "offline") {
        transitionToRestored();
      } else {
        setConsecutiveFailures(0);
      }
    } else {
      const failures = consecutiveFailures() + 1;
      setConsecutiveFailures(failures);

      // On initial check, immediately show reconnecting on first failure
      // This ensures the banner shows quickly when API is down at page load
      if (isInitialCheck || failures >= maxConsecutiveFailures) {
        const currentState = state();
        if (currentState === "online") {
          transitionToReconnecting();
        } else if (currentState === "reconnecting" && failures >= maxConsecutiveFailures) {
          transitionToOffline();
        }
      }
    }
  };

  const initialHeartbeat = async () => {
    const success = await performHeartbeatCheck();
    if (isMounted) handleHeartbeatResult(success, true);
  };

  const startHeartbeat = () => {
    if (isServer) return;

    clearHeartbeatInterval();

    const runHeartbeat = async () => {
      if (document.hidden || !navigator.onLine) return;
      const success = await performHeartbeatCheck();
      handleHeartbeatResult(success);
    };

    void runHeartbeat();
    heartbeatInterval = setInterval(() => void runHeartbeat(), heartbeatIntervalMs);
  };

  const handleOnline = () => {
    const currentState = state();
    // Only transition if we're actually in an offline state
    if (currentState === "offline") {
      transitionToReconnecting();
    }
  };

  const handleOffline = () => {
    transitionToOffline();
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      clearHeartbeatInterval();
    } else {
      const currentState = state();
      if (currentState === "reconnecting" || currentState === "offline") {
        startHeartbeat();
      }
    }
  };

  const transitionToReconnectingIfNeeded = () => {
    const currentState = state();
    if (currentState === "online" || currentState === "restored") {
      transitionToReconnecting();
    }
  };

  const handleStreamingError = (event: CustomEvent<GlobalEventData<"connectivity-streaming-error">>) => {
    const { error, code } = event.detail;
    void stAnalytics.track("connectivity_error", {
      error,
      code,
      errorType: "streaming",
    });
    transitionToReconnectingIfNeeded();
  };

  const handleApiError = (event: CustomEvent<GlobalEventData<"connectivity-api-error">>) => {
    const { error, code } = event.detail;
    void stAnalytics.track("connectivity_error", {
      error,
      code,
      errorType: "api",
    });
    transitionToReconnectingIfNeeded();
    // Trigger heartbeat to confirm connectivity (skipped when browser reports offline)
    void forceCheck();
  };

  if (!isServer) {
    onMount(() => {
      isMounted = true;

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      const unsubscribeStreamingError = subscribeToGlobalEvent("connectivity-streaming-error", handleStreamingError);
      const unsubscribeApiError = subscribeToGlobalEvent("connectivity-api-error", handleApiError);

      if (!navigator.onLine) {
        transitionToOffline();
      } else {
        // Run immediate heartbeat check on mount to detect API unavailability early
        // This ensures the banner shows quickly when API is down at page load
        void initialHeartbeat();
      }

      onCleanup(() => {
        isMounted = false;
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        unsubscribeStreamingError();
        unsubscribeApiError();
        clearHeartbeatInterval();
        clearRestoredTimeout();
      });
    });
  }

  const forceCheck = async () => {
    if (isServer || forceCheckPending || !navigator.onLine) return;

    forceCheckPending = true;
    try {
      const success = await performHeartbeatCheck();
      if (isMounted) {
        handleHeartbeatResult(success);
      }
    } finally {
      forceCheckPending = false;
    }
  };

  return {
    state,
  };
};
